import { useEffect, useMemo } from 'react';

import { Box, Checkbox, Divider, Stack, Typography } from '@mui/material';

import { useGetDatesQuery } from 'state/apiSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectCurrentProjectInfo,
  selectMapZoom,
  selectRoadFilters,
  selectTargetDate,
  selectTypicalMonth,
  selectWorkflow,
  setRoadFilters,
} from 'state/workflowSlice';

import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { StlPopover } from 'features/common/StlPopup/StlPopover';
import StlSwitch from 'features/common/StlSwitch/StlSwitch';
import { classNames } from 'features/common/utils';
import { MapControl } from 'features/map/utils/mapControls';
import { findDate } from 'features/task_bar/DatePicker';
import { WorkflowTimeline } from 'features/workflow_timeline/WorkflowTimeline';

import { kRoadClassControlId } from '../../utils/map_constants';
import {
  FRC_LABELS,
  FRC_LINK_IDS,
  HIGHWAY_TO_FRC_ID,
  RESIDENTIAL_FRC,
  allowedRoadClassFilterIdsFromHighwayTypes,
  filterKeysBaseline,
  frcAtZoom,
  isLinkRoadClassFilterId,
  toRoadClassFilterId,
} from './utils';

import styles from './RoadClassTooltip.module.css';

interface IRoadClassTooltip {
  open: boolean;
  anchorEl: HTMLElement;
  handleVisSwitch: (val: boolean) => void;
}

export function RoadClassTooltip({
  open,
  anchorEl,
  handleVisSwitch,
}: IRoadClassTooltip) {
  const dispatch = useAppDispatch();

  const userProject = useAppSelector(selectCurrentProjectInfo);
  const mapZoom = useAppSelector(selectMapZoom);
  const workflow = useAppSelector(selectWorkflow);
  const targetDate = useAppSelector(selectTargetDate);
  const roadFilters = useAppSelector(selectRoadFilters);
  const typicalMonth = useAppSelector(selectTypicalMonth);

  const { data: datesInfo, isLoading } = useGetDatesQuery(
    userProject?.project_slug,
    {
      skip: !userProject?.project_slug || !targetDate,
    },
  );

  const allowedFilterIds = useMemo(
    () => allowedRoadClassFilterIdsFromHighwayTypes(userProject?.highway_types),
    [userProject?.highway_types],
  );

  const baselineKeys = useMemo(
    () => filterKeysBaseline(allowedFilterIds),
    [allowedFilterIds],
  );

  const visibleMainFrcEntries = useMemo(
    () =>
      Object.entries(FRC_LABELS).filter(([key]) => allowedFilterIds.has(key)),
    [allowedFilterIds],
  );

  const visibleLinkFrcEntries = useMemo(
    () =>
      Object.entries(FRC_LABELS).filter(
        ([key]) =>
          key !== String(RESIDENTIAL_FRC) &&
          allowedFilterIds.has(toRoadClassFilterId(key, true)),
      ),
    [allowedFilterIds],
  );
  const hasProjectLinkTypes = visibleLinkFrcEntries.length > 0;
  const roadClasses = roadFilters ?? baselineKeys;

  useEffect(() => {
    MapControl.setValue(open, kRoadClassControlId);
  }, [open]);

  const dayInfo =
    targetDate && datesInfo ? findDate(targetDate, datesInfo) : undefined;

  const monthBasedSelection = Boolean(!targetDate && typicalMonth);

  const isRt = monthBasedSelection
    ? false
    : targetDate
      ? isLoading || !datesInfo
        ? undefined
        : !dayInfo?.is_complete_vnrt
      : undefined;

  const disableResidential =
    isRt === undefined
      ? undefined
      : (workflow === WorkflowTimeline &&
          userProject?.disable_timeline_residential_roads) ||
        isRt;

  useEffect(() => {
    if (!userProject?.highway_types) return;
    if (!roadFilters?.length) return;

    const getFinalFilters = (): string[] => {
      const filters = new Set(
        roadFilters.filter((id) => allowedFilterIds.has(id)),
      );

      // edge case: if realtime and tetriary, add residential in the background (there's no residential_link)
      if (isRt && disableResidential) {
        const tertiaryId = toRoadClassFilterId(
          HIGHWAY_TO_FRC_ID.tertiary,
          false,
        );
        const residentialId = toRoadClassFilterId(
          HIGHWAY_TO_FRC_ID.residential,
          false,
        );

        if (filters.has(tertiaryId)) {
          filters.add(residentialId);
        }
      }

      return [...filters];
    };

    const filtered = getFinalFilters();

    if (filtered.length !== roadFilters.length) {
      dispatch(setRoadFilters(filtered.length > 0 ? filtered : baselineKeys));
    }
  }, [
    userProject?.highway_types,
    allowedFilterIds,
    baselineKeys,
    roadFilters,
    isRt,
    disableResidential,
  ]);

  const maxFrc =
    disableResidential === undefined
      ? undefined
      : frcAtZoom(mapZoom, disableResidential);

  const isRowDisabledByZoom = (frcKey: number) =>
    maxFrc == null || maxFrc < frcKey;

  const isMainRowDisabled = (frcKey: number) =>
    isRowDisabledByZoom(frcKey) || !allowedFilterIds.has(String(frcKey));

  const isLinkRowDisabled = (frcKey: number) =>
    isRowDisabledByZoom(frcKey) ||
    !allowedFilterIds.has(toRoadClassFilterId(frcKey, true));

  const toggleFilter = (filterId: string) => {
    const next = roadClasses.includes(filterId)
      ? roadClasses.filter((item) => item !== filterId)
      : [...roadClasses, filterId];

    dispatch(setRoadFilters(next));
  };

  const toggleLinks = () => {
    const nextFilters = roadClasses.some(isLinkRoadClassFilterId)
      ? roadClasses.filter((id) => !isLinkRoadClassFilterId(id))
      : [...new Set([...roadClasses, ...FRC_LINK_IDS])];

    dispatch(setRoadFilters(nextFilters));
  };

  const enableLinks = roadClasses.some(isLinkRoadClassFilterId);

  if (!open) return null;

  return (
    <StlPopover
      title="Road Classes"
      anchorEl={anchorEl}
      isOpen={open}
      onClose={() => handleVisSwitch(false)}
      classes={[styles.popover]}
    >
      <Stack display="flex" className={styles.popoverBody}>
        <Typography className={styles.subtitle} variant="body2">
          Road classes may automatically be hidden at certain zoom levels.
        </Typography>
        <div>
          {visibleMainFrcEntries.map(([key, label]) => {
            const disabled = isMainRowDisabled(Number(key));
            return (
              <RoadClassOption
                key={key}
                label={label}
                checked={roadClasses.includes(key)}
                disabled={disabled}
                onToggle={() => toggleFilter(key)}
              />
            );
          })}
        </div>
        {hasProjectLinkTypes && (
          <>
            <Divider />
            <Box display="flex" className={styles.header}>
              <Typography variant="h4">Ramps and Links</Typography>
              <StlSwitch
                className={styles.switch}
                checked={enableLinks}
                onChange={toggleLinks}
              />
            </Box>
            {enableLinks && (
              <div className={styles.optionsLink}>
                {visibleLinkFrcEntries.map(([key, label]) => {
                  const filterId = toRoadClassFilterId(key, true);
                  const disabled = isLinkRowDisabled(Number(key));

                  return (
                    <RoadClassOption
                      key={filterId}
                      label={label}
                      checked={roadClasses.includes(filterId)}
                      disabled={disabled}
                      onToggle={() => toggleFilter(filterId)}
                    />
                  );
                })}
              </div>
            )}
          </>
        )}
      </Stack>
    </StlPopover>
  );
}

function RoadClassOption({
  label,
  checked,
  disabled,
  onToggle,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <Box
      display="flex"
      className={classNames(styles.option, disabled && styles.optionDisabled)}
      onClick={() => !disabled && onToggle()}
    >
      <Checkbox
        disabled={disabled}
        checked={checked}
        onChange={onToggle}
        size="small"
      />
      <Typography variant="body2">{label}</Typography>
      <Box className={styles.iconWrapper}>
        <FontAwesomeIcon name="eye-slash" />
      </Box>
    </Box>
  );
}
